// Textos-base do site. Marcadores {{...}} = dado factual a confirmar pela autora.
// Pendências só aparecem na pré-visualização; no domínio público ficam ocultas.

export const SHOW_PENDING =
    typeof window !== "undefined" && /preview\.emergentagent\.com|localhost/.test(window.location.hostname);
export const isPending = (t) => /\{\{[^}]+\}\}/.test(String(t || ""));

const U = (id) =>
    `https://images.unsplash.com/${id}?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=2000`;

export const IMG = {
    hero: U("photo-1604092257464-d0d7f0d167bc"),
    radar: U("photo-1526628953301-3e589a6a8b74"),
    urbano: U("photo-1472146936668-d987bf0a6e38"),
    percepcao: U("photo-1521194263619-39ecc5b55c61"),
    producao: U("photo-1763315156830-07870b159121"),
    humano: U("photo-1604074309324-72543c99d59c"),
    docencia: U("photo-1588702547954-4800ead296ef"),
    pratica: U("photo-1766788467611-99e43eccef94"),
    rtl: U("photo-1520464399004-1f1e8e938bb3"),
    sobre: U("photo-1591734183571-b4310bcc25aa"),
    publicacoes: U("photo-1769794370964-78412732f1cd"),
    contato: U("photo-1516397281156-ca07cf9746fc"),
};

export const NAV = [
    { label: "Início", to: "/" },
    { label: "Radar", to: "/radar", radar: true },
    { label: "Pesquisa", to: "/pesquisa" },
    { label: "Publicações", to: "/publicacoes" },
    { label: "Docência", to: "/docencia" },
    { label: "Sobre", to: "/sobre" },
    { label: "Contato", to: "/contato" },
];

export const PRINCIPIOS = {
    pergunta:
        "Como agir quando os problemas estão conectados e se retroalimentam de formas nem sempre visíveis, os recursos são limitados, diferentes partes envolvidas e afetadas percebem a realidade por lentes distintas e o próprio contexto segue mudando — por vezes de forma abrupta — enquanto ainda tentamos compreendê-lo?",
    lentes:
        "Ampliar as lentes não elimina a incerteza. Ajuda a reconhecer melhor o que sabemos, o que supomos e o que ainda não conseguimos enxergar.",
    realidade: "Toda leitura é feita sobre uma realidade que já começou a mudar.",
    problema:
        "Nem sempre o problema que aparece primeiro revela as relações e condições que contribuem para sua permanência ou recorrência.",
    central:
        "Sistemas humanos complexos não cabem em respostas universais. Contextos se transformam, perspectivas diferem, efeitos podem se retroalimentar e parte da realidade permanece fora do nosso campo de visão. Pesquisar, para mim, é ampliar as lentes sem confundir ampliação de percepção com certeza absoluta.",
    sistemas:
        "Em sistemas humanos complexos, decisões e resultados emergem de contextos dinâmicos, parcialmente observáveis e atravessados por múltiplas variáveis, relações e retroalimentações.",
    causalidade:
        "Compreender um resultado exige distinguir intenção, decisão, contexto e causalidade.",
    humano: "Entre informação e ação existe o ser humano.",
    valor: "Lucro contábil e criação de valor não são necessariamente a mesma coisa.",
    valorPergunta:
        "Se existe lucro para uma organização enquanto o sistema do qual ela depende perde valor, o resultado foi integralmente positivo?",
    mimetica:
        "Essa escolha responde às condições concretas da organização ou também pode estar sendo influenciada pela necessidade de acompanhar, superar ou parecer semelhante ao outro?",
    autopercepcao:
        "Se não reconhecemos minimamente as lentes através das quais percebemos, até que ponto conseguimos construir decisões coletivas conscientes?",
    naoMede: "Aquilo que um modelo não mede não deixa necessariamente de existir.",
    coletivo:
        "Como construir organizações e sistemas capazes de produzir valor não apenas para quem decide, mas também para o conjunto do qual fazem parte?",
};

export const HERO = {
    name: "Raquel Tomaselli Lauria",
    role: "Doutoranda em Economia Circular e Produção Sustentável",
    title: "Pesquisa aplicada sobre adaptação, complexidade sistêmica e criação de valor em contextos de policrise",
    lead:
        "Investigo como pequenas empresas percebem e se reorganizam em contextos de policrise, marcados por transformações simultâneas, retroalimentações nem sempre visíveis e rupturas econômicas, tecnológicas, ambientais, políticas, sociais e geracionais capazes de reconfigurar tanto a organização quanto o ecossistema em que ela opera. Também observo como identidade, comportamento coletivo e processos não plenamente conscientes influenciam o que percebemos e aquilo que conseguimos construir em conjunto.",
    signature: ["Observar", "Investigar", "Aplicar", "Ensinar"],
    ctas: [
        { label: "Pesquisa", to: "/pesquisa", primary: true },
        { label: "Docência", to: "/docencia" },
        { label: "Publicações", to: "/publicacoes" },
    ],
};

export const RETRO = {
    eyebrow: "Observar · perguntar · investigar",
    titulo: "Problemas conectados, efeitos que retornam.",
    policrise:
        "Policrise não é apenas a coexistência de várias crises. Interessam as situações em que diferentes fatores se conectam, alteram outros elementos e geram novos efeitos — e parte desses efeitos retorna ao sistema por caminhos inicialmente pouco visíveis.",
    sequencia: ["se conectam", "alteram outros elementos", "geram novos efeitos", "retornam ao sistema"],
    contexto:
        "O contexto não é cenário estático. É parte ativa do sistema: muda, reage, sofre interferências, produz efeitos e pode se transformar durante o próprio processo de análise.",
    nota: "Dimensões ilustrativas. As dimensões e relações relevantes mudam conforme o contexto.",
    nota2: "Representação conceitual — não constitui modelo causal ou estrutura fixa.",
};

export const INVESTIGACAO = {
    eyebrow: "Pesquisa",
    title: "O que se investiga quando o problema não está isolado.",
    paragraphs: [
        "A pesquisa parte das pequenas empresas — organizações que operam com recursos limitados, informação incompleta e pouca margem para errar duas vezes. Nelas, uma variação de custo, uma mudança regulatória, uma nova tecnologia ou um desgaste nas relações internas raramente permanecem restritos ao ponto onde surgiram.",
        "Interessa compreender como esses fatores se encadeiam: como uma resposta a um problema imediato pode alimentar outro, menos visível, que retorna mais tarde com outra forma — custo, conflito, escassez, perda de confiança ou de capacidade de adaptação.",
    ],
    perguntas: [
        "Como pequenas empresas percebem e nomeiam os problemas que enfrentam?",
        "Que retroalimentações ficam fora do campo de visão no momento da decisão?",
        "O que muda na leitura de um problema quando o contexto é tratado como parte ativa do sistema?",
        "Como distinguir o problema que se torna visível das relações e condições que contribuem para sua permanência ou recorrência?",
    ],
};

export const HUMANO = {
    eyebrow: "Pesquisa · complexidade humana",
    paragraphs: [
        "Organizações não são feitas apenas de números, tecnologia, processos e modelos supostamente racionais. Pessoas e grupos podem perceber uma mesma situação por lentes diferentes — e agir de formas distintas diante dos mesmos dados.",
        "Uma percepção orienta uma escolha, a escolha altera o contexto, e o contexto alterado produz novas percepções.",
    ],
    lentes: [
        "identidade", "pertencimento", "reconhecimento", "relações", "poder",
        "comportamento coletivo", "vieses", "ansiedade mimética", "fatores conscientes e não plenamente conscientes",
    ],
    conexao:
        "A dimensão humana não é uma digressão psicológica: atravessa a forma como pequenas empresas leem riscos, adotam tecnologias, inovam, competem e se adaptam — e, por isso, participa dos próprios sistemas econômicos.",
    nota:
        "A investigação observa fenômenos, relações e processos sem reduzir decisões complexas a características individuais. O objetivo é ampliar a compreensão dos sistemas.",
};

export const LENTES_HUMANAS = [
    {
        k: "Autopercepção",
        d: "Algumas escolhas também podem ser atravessadas por pertencimento, reconhecimento, identidade, comparação, expectativas coletivas e necessidade de diferenciação. Reconhecer as próprias lentes é parte das condições para decidir em conjunto.",
        tipo: "lente-teorica",
        q: PRINCIPIOS.autopercepcao,
    },
    {
        k: "Comportamento mimético",
        tipo: "lente-teorica",
        d: "Uma lente possível — não uma teoria explicativa universal — para situações como a adoção de tecnologias porque todos adotaram, o crescimento apenas para acompanhar concorrentes, a confusão entre visibilidade e criação de valor ou a reprodução de modelos incompatíveis com a realidade da própria organização.",
        q: PRINCIPIOS.mimetica,
    },
    {
        k: "Individuação",
        d: "Na psicologia analítica, individuação descreve um processo de diferenciação e integração da personalidade. Aqui, o conceito aparece como uma das lentes possíveis para refletir sobre identidade, autopercepção, autonomia e participação consciente no coletivo.",
        q: "Conhecer melhor a si mesmo pode ampliar a capacidade de reconhecer as próprias influências e participar de decisões coletivas com maior consciência.",
        afirmacao: true,
        tipo: "lente-teorica",
    },
];

export const COLETIVO = {
    movimento: ["perceber a si", "perceber o sistema", "perceber o outro", "perceber consequências", "construir em conjunto"],
};

export const VALOR = {
    eyebrow: "Doutorado · Economia circular e produção sustentável",
    lead:
        "Não se trata de afirmar que lucro significa dano. Trata-se de ampliar a análise: uma organização pode apresentar resultado financeiro positivo enquanto determinados custos ambientais ou sociais permanecem fora de sua contabilidade — e um efeito tratado hoje como “externo” pode retornar depois em forma de escassez, aumento de custos, regulação, perda de produtividade ou outros riscos.",
    perguntas: [
        "Quais custos foram contabilizados?",
        "Quais permaneceram externos à contabilidade?",
        "Quem recebe os benefícios?",
        "Quem absorve os custos?",
        "Em qual horizonte temporal?",
        "Que efeitos podem retornar futuramente ao próprio sistema?",
    ],
    conceitos: [
        "externalidades", "capital natural", "custos ambientais e sociais", "circularidade",
        "dependência de recursos", "criação e destruição de valor", "horizonte temporal", "responsabilidade intergeracional",
    ],
    lente:
        "Economia circular e sustentabilidade não são apresentadas aqui como respostas universais, mas também como lentes e ferramentas cuja pertinência depende do contexto.",
};

export const AGENDA = {
    eyebrow: "Agenda de pesquisa em desenvolvimento",
    title: "Questões em investigação",
    lead:
        "As perguntas abaixo não compõem uma teoria fechada nem uma metodologia validada. São hipóteses em construção — e a própria mudança das perguntas ao longo da pesquisa faz parte do processo.",
    frentes: [
        { t: "Policrise e retroalimentações", d: "Como fatores econômicos, tecnológicos, ambientais e humanos se conectam e retornam ao sistema das pequenas empresas." },
        { t: "Contexto em transformação", d: "Como analisar uma realidade que muda, reage e pode sofrer rupturas durante a própria interpretação." },
        { t: "Complexidade humana nas organizações", d: "Percepção, identidade, comparação e comportamento coletivo como variáveis da decisão." },
        { t: "Valor, externalidades e horizonte temporal", d: "A diferença entre lucro contábil e criação de valor; custos que ficam fora da contabilidade e podem retornar depois." },
        { t: "Circularidade como lente", d: "Economia circular e sustentabilidade avaliadas como ferramentas e hipóteses conforme o contexto." },
        { t: "Tecnologia e interdependências", d: "O que uma tecnologia resolve, desloca ou cria em sistemas já interdependentes." },
    ],
    futuro: "Estudos futuros serão incorporados a esta agenda à medida que forem desenvolvidos.",
    metodo: "{{a confirmar: abordagem metodológica e vínculo da pesquisa}}",
};

export const BRASIL = {
    eyebrow: "Contexto · Brasil diante do mundo",
    lead:
        "Um sistema pode reunir vantagens estruturais relevantes e, simultaneamente, carregar vulnerabilidades que interagem entre si. Os dados abaixo oferecem contexto e perguntas — não provam uma tese previamente escolhida. Fontes oficiais (Banco Mundial e Ember), com o ano de cada medição visível.",
};

export const TEMAS = [
    { k: "policrise", q: "O que muda quando as crises deixam de ser episódios e passam a interagir?" },
    { k: "comportamento", q: "Quanto das escolhas organizacionais responde à comparação com o outro?", slugs: ["amo-ansiedade-mimetica-organizacional"] },
    { k: "governança", q: "O que acontece antes de a governança formal começar?", slugs: ["mcn-captura-narrativa-sistemas-familiares"] },
    { k: "continuidade", q: "O que permite a uma organização permanecer, com recursos limitados e ao longo do tempo?", slugs: ["ciencia-da-continuidade-administracao-policrise-estrutural"] },
    { k: "sustentabilidade e economia circular", q: "O que um resultado positivo deixa fora da conta?", aprofundamento: true },
];

export const PUBLICACOES = {
    eyebrow: "Publicações",
    title: "Temas e perguntas em desenvolvimento.",
    lead:
        "Textos publicados em ordem cronológica, com títulos e links originais. Os trabalhos se relacionam, mas não formam uma cadeia linear nem uma teoria única: são investigações distintas atravessadas por questões comuns.",
    temasTitulo: "Questões que vêm atravessando a produção",
};

export const DISCIPLINA = {
    status: "Disciplina autoral em desenvolvimento",
    nome: "Adaptação Organizacional em Contextos de Policrise",
    subtitulo: "Complexidade sistêmica, fatores humanos e criação de valor",
    proposta:
        "A disciplina toma pequenas empresas como campo privilegiado de observação para investigar como organizações percebem, interpretam e se reorganizam diante de transformações simultâneas, retroalimentações nem sempre visíveis e rupturas que podem reconfigurar o sistema em que operam.",
    proposta2:
        "Diferentes lentes são aproximadas para investigar esses contextos — complexidade sistêmica, fatores humanos, tecnologia, sustentabilidade e criação de valor — sem pressupor que uma única perspectiva seja capaz de explicar o sistema inteiro.",
    adaptacao:
        "Adaptação, aqui, não significa simples reação nem retorno a um estado anterior, mas reorganização contínua de um sistema que também se transforma — e no qual pessoas, percepções e relações fazem parte da própria complexidade.",
    objetivo:
        "Objetivo pedagógico: ampliar e revisar continuamente as lentes utilizadas para interpretar sistemas em transformação, reconhecer relações e aprender com os efeitos produzidos ao longo do próprio processo.",
    principio:
        "Aprender não significa chegar a um ponto em que finalmente possuímos todas as respostas. Significa ampliar a capacidade de perceber relações, sustentar perguntas, confrontar perspectivas, lidar com incerteza, observar consequências e revisar a própria compreensão quando necessário.",
    mapaLegenda: "Não há sequência fixa. Percepção, interpretação, reorganização, efeitos e revisão podem ocorrer simultaneamente, retroalimentar-se e modificar o próprio sistema observado.",
    mapaNota: "Representação pedagógica — não constitui metodologia científica validada.",
    perguntas: [
        "O que pode mudar no próprio contexto enquanto ainda tentamos compreendê-lo?",
        "Que relações e retroalimentações permanecem fora do campo de visão quando observamos apenas o efeito mais imediato?",
        "Como diferentes perspectivas alteram a própria formulação do problema?",
    ],
    eixos: [
        { t: "Policrise e complexidade sistêmica", d: "Transformações simultâneas, relações não lineares e retroalimentações cujos efeitos podem retornar ao sistema e alterar aquilo que inicialmente os produziu." },
        { t: "Adaptação e contexto em transformação", d: "Como organizações percebem e se reorganizam enquanto as próprias condições econômicas, sociais, políticas, ambientais e tecnológicas também continuam mudando." },
        { t: "Fatores humanos e percepção", d: "Como percepção, identidade, pertencimento, comparação, reconhecimento, comportamento coletivo e processos não plenamente conscientes participam da forma como indivíduos e organizações interpretam aquilo que acontece." },
        { t: "Tecnologia, IA e disrupção sistêmica", d: "Investigar o que novas tecnologias resolvem, deslocam, aceleram, tornam obsoleto ou passam a criar em sistemas já sujeitos a transformação." },
        { t: "Sustentabilidade, economia circular e produção sustentável", d: "Investigar possibilidades de reorganização de recursos, fluxos e processos sem tratar sustentabilidade ou circularidade como soluções universais." },
        { t: "Criação de valor e efeitos deslocados", d: "Observar como uma resposta pode gerar valor em determinado ponto do sistema e, simultaneamente, produzir novas dependências, impactos ou consequências em outro." },
    ],
    trabalho: {
        t: "Tecnologia e desenho do trabalho",
        d: "Tecnologias digitais e automação podem ampliar a capacidade de execução, integrar tarefas antes separadas e modificar a própria definição de uma função. Isso exige repensar o desenho do trabalho e a forma como valor, responsabilidade e capacidade de adaptação são compreendidos.",
        q: [
            "Como redesenhar funções quando a tecnologia modifica aquilo que uma pessoa consegue executar?",
            "Como distinguir integração produtiva de tarefas de simples sobrecarga?",
            "Como construir estruturas mais eficientes sem confundir eficiência com transferência indiscriminada de responsabilidades?",
        ],
    },
    transversais: {
        lead: "Algumas questões atravessam diferentes dimensões da disciplina e ajudam a observar como transformação tecnológica, fatores humanos e criação de valor se relacionam dentro das organizações.",
        itens: [
            {
                id: "comparacao",
                t: "Comparação, reconhecimento e percepção de suficiência",
                d: "Referências externas, status, pertencimento, reconhecimento e comportamento mimético podem alterar aquilo que indivíduos e organizações passam a interpretar como valor, sucesso ou insuficiência.",
                q: [
                    "Quanto do crescimento que buscamos responde a necessidades concretas e quanto responde a referências externas que continuam se deslocando?",
                    "Em que condições a busca por reconhecimento passa a alimentar ciclos persistentes de comparação e insatisfação?",
                ],
            },
            {
                id: "identidade",
                t: "Identidade profissional, adaptação e geração de valor",
                d: "Em contextos de transformação, experiência acumulada continua relevante, mas não garante que tarefas, competências ou fronteiras profissionais permaneçam com o mesmo valor. Tecnologia, automação e novos modelos de negócio podem recombinar funções e alterar aquilo que a organização precisa. O desafio passa a ser reconhecer o que mudou, revisar a própria contribuição e encontrar novas formas de gerar valor.",
                nota: "Comparação hierárquica pode deslocar a atenção da questão central: qual contribuição continua gerando valor quando tarefas, tecnologias e necessidades mudam?",
                q: [
                    "Quando a experiência acumulada se transforma em capacidade de adaptação e quando se transforma em apego a uma função que o contexto já modificou?",
                    "Como identificar novas formas de gerar valor quando tecnologia e automação alteram tarefas que durante anos definiram uma posição profissional?",
                    "Quanto da energia organizacional é direcionada à criação de valor e quanto é consumida por comparação, status e disputa por reconhecimento?",
                    "Como distinguir ampliação produtiva de uma função de sobrecarga inadequada?",
                ],
            },
        ],
    },
    individuacaoNota:
        "Autopercepção não elimina vieses nem transforma alguém em observador neutro; pode, porém, ampliar a capacidade de reconhecer algumas das forças que participam da própria interpretação.",
    individuacaoDialogo: "Essa reflexão dialoga com a investigação mais ampla sobre limites da percepção desenvolvida na agenda de pesquisa.",
    formatos: [
        { t: "Videoaulas", s: "em preparação" },
        { t: "Materiais didáticos", s: "em preparação" },
        { t: "Estudos de caso", s: "em preparação" },
        { t: "Atividades e avaliações", s: "em preparação" },
    ],
};

export const DOCENCIA = {
    eyebrow: "Docência",
    title: "Transformar perguntas em percursos de aprendizagem.",
    lead:
        "A docência, para mim, nasce da pesquisa. Questões que investigo podem se transformar em percursos de aprendizagem que ampliem a capacidade de perceber relações, confrontar perspectivas, reconhecer limites de interpretação e revisar premissas enquanto a própria realidade se transforma.",
    lead2:
        "Não parto da ideia de que exista um ponto em que a realidade esteja completamente conhecida ou uma resposta definitiva se torne evidente. Em sistemas humanos complexos, aprender também envolve sustentar perguntas, lidar com informação incompleta, observar efeitos e permanecer disposto a rever a própria compreensão.",
    principios: [
        "Perguntas difíceis para realidades que não cabem em respostas definitivas.",
        "Observar sistemas, relações, evidências, retroalimentações e consequências sem reduzir a complexidade humana.",
        "Reconhecer que toda leitura é parcial e pode precisar ser revista à medida que o contexto, as evidências e os efeitos também se transformam.",
    ],
    autoria: {
        titulo: "Professora autora e conteudista EAD",
        lead: "Desenvolvimento de conteúdos e percursos formativos a partir da pesquisa:",
        itens: [
            "Criação de disciplinas e ementas",
            "Materiais didáticos",
            "Estudos de caso",
            "Roteiros de videoaulas",
            "Videoaulas",
            "Atividades e avaliações",
        ],
    },
    temas: [
        "Policrise e adaptação organizacional",
        "Complexidade sistêmica e retroalimentações",
        "Fatores humanos e percepção",
        "Tecnologia, IA e disrupção",
        "Sustentabilidade, economia circular e criação de valor",
    ],
};

export const PRATICA = {
    eyebrow: "Experiência e prática",
    text:
        "A trajetória profissional inclui negócios, gestão, imóveis, avaliação e projetos especiais. O contato com situações empresariais concretas também alimenta perguntas que atravessam a pesquisa.",
};

export const RTL = {
    eyebrow: "Negócios imobiliários e projetos especiais",
    text: "A atuação profissional nessa área é desenvolvida pela RTL Amazônia — Referência Técnica e Logística da Amazônia.",
    cta: "Conhecer a RTL Amazônia",
    url: "https://rtlamazonia.com.br",
    label: "rtlamazonia.com.br",
};

export const SOBRE = {
    eyebrow: "Sobre",
    title: "Perguntas difíceis, lentes em diálogo.",
    curta:
        "Doutoranda em Economia Circular e Produção Sustentável, desenvolvo pesquisa aplicada sobre adaptação, complexidade sistêmica e criação de valor em contextos de policrise.",
    producao:
        "No doutorado, investigo como práticas de economia circular e produção sustentável se relacionam com a capacidade adaptativa organizacional. Fatores humanos e percepção, assim como tecnologia e disrupção, atravessam a investigação como dimensões transversais.",
    docencia:
        "Como professora autora e conteudista EAD, transformo questões da pesquisa em disciplinas, materiais didáticos, estudos de caso e videoaulas.",
    publicacoes:
        "A produção recente reúne textos sobre policrise, comportamento organizacional, governança e continuidade — investigações distintas, atravessadas por questões comuns.",
    formacao: [
        { t: "Doutorado em Economia Circular e Produção Sustentável", d: "Doutoranda" },
    ],
    links: [
        { t: "SSRN · Ansiedade Mimética Organizacional", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6631700" },
        { t: "SSRN · Captura Narrativa em sistemas familiares", url: "https://papers.ssrn.com/sol3/papers.cfm?abstract_id=6696580" },
    ],
};

export const COOPERACAO = {
    eyebrow: "Pesquisa, Docência & Cooperação",
    title: "Construir algo em conjunto.",
    lead:
        "Abertura para diálogo com pesquisadores, universidades, instituições e organizações interessadas em pesquisa, colaboração acadêmica, desenvolvimento de estudos, criação de disciplinas, conteúdos EAD, estudos de caso e participação em projetos.",
    temas: [
        "Pesquisa e colaboração acadêmica",
        "Criação de disciplinas e conteúdos EAD",
        "Estudos de caso e desenvolvimento de estudos",
        "Participação em projetos",
    ],
    email: "sustain2090@gmail.com",
    privacidade: "Os dados enviados serão utilizados exclusivamente para responder a este contato.",
    comercial: "Assuntos comerciais, imóveis e projetos especiais:",
};

export const RADAR = {
    eyebrow: "Radar",
    title: "Radar",
    lead: "Pesquisas, dados e acontecimentos para acompanhar uma realidade em transformação.",
    aviso:
        "Curadoria de pesquisas, dados e acontecimentos acompanhada de perguntas de leitura. Não pretende oferecer previsões ou conclusões definitivas.",
    secoes: {
        what: "O que está acontecendo",
        evidence: "O que a evidência disponível mostra",
        why: "Por que importa",
        question: "A pergunta que fica",
        relation: "Relações com pesquisa ou ensino",
    },
    categorias: {
        amazonia: "Amazônia",
        clima: "Clima",
        economia: "Economia",
        "pequenas-empresas": "Pequenos negócios",
        sustentabilidade: "Sustentabilidade",
        "economia-circular": "Economia circular",
        tecnologia: "Tecnologia",
        sociedade: "Sociedade",
    },
    vazio: "As primeiras leituras do Radar estão em curadoria.",
};

export const PESQUISA_PAGE = {
    eyebrow: "Pesquisa",
    title: "Agenda de pesquisa em desenvolvimento",
    lead:
        "Pequenas empresas em contextos de policrise: problemas conectados, recursos limitados, percepções distintas e contextos que mudam durante a própria análise.",
};

// Estrutura interna preservada (não exibida publicamente). Antes chamada ARANDU Circular;
// as frentes foram integradas em AGENDA. Pronta para receber um nome próprio no futuro.
export const _INTERNO_PROGRAMA = {
    nomeProvisorio: "ARANDU Circular",
    definicao: "Programa autoral de pesquisa aplicada em desenvolvimento por Raquel Tomaselli Lauria.",
    papeis: { observacao: "Radar", pesquisaEstruturada: "programa (nome em definição)" },
};

// Estrutura pública da investigação (níveis distintos; nada aqui é resultado demonstrado).
export const INVESTIGACAO_NIVEIS = {
    questao: {
        label: "Questão de pesquisa",
        text: "Como pequenas empresas podem ampliar sua capacidade de adaptação em contextos de policrise quando operam com recursos limitados, interdependências nem sempre visíveis e condições que se transformam durante o próprio processo de decisão?",
    },
    proposicao: {
        label: "Proposição central",
        status: "em construção",
        text: "Em contextos de policrise, resiliência não significa estabilidade permanente, mas capacidade dinâmica de perceber mudanças, revisar premissas e reorganizar relações e recursos à medida que o contexto se transforma.",
    },
    hipotese: {
        label: "Hipótese",
        status: "de trabalho",
        text: "Pequenas empresas que desenvolvem mecanismos explícitos de leitura sistêmica e revisão periódica de premissas — reconhecendo dependências críticas, retroalimentações, externalidades e sinais de mudança — tendem a identificar vulnerabilidades e adaptar suas decisões mais cedo do que organizações orientadas predominantemente por leituras lineares, estáticas e de curto prazo.",
    },
    aviso: "Proposição e hipótese não são resultados demonstrados: orientam a investigação e serão revistas à medida que as evidências forem examinadas.",
    amplaLabel: "Pergunta ampla, de orientação filosófico-sistêmica",
};

export const LIMITES = {
    eyebrow: "Limites da percepção",
    pergunta: "O que percebemos é a realidade inteira ou apenas aquilo que nossas condições, referências e instrumentos nos permitem perceber?",
    caverna:
        "Na Alegoria da Caverna, apresentada por Platão na República, o problema não é apenas aquilo que vemos, mas o risco de confundirmos uma percepção parcial com a totalidade do real. Em sistemas complexos, essa questão permanece relevante: observamos a realidade por meio de instrumentos, referências, experiências, linguagens e limites próprios.",
    investigador:
        "Quem investiga também observa a partir de determinadas lentes. Reconhecer essa limitação não enfraquece a pesquisa; aumenta a importância do método, do confronto entre perspectivas e da disposição para revisar premissas.",
    pontes: ["pensamento sistêmico", "psicologia organizacional", "autopercepção", "dados e tecnologia", "economia e externalidades"],
};

export const CONTEXTO = {
    eyebrow: "Policrise e contexto em transformação",
    title: "Quando o terreno muda durante a decisão.",
    paragraphs: [
        "Mudanças econômicas, tecnológicas, climáticas, regulatórias e sociais não chegam às pequenas empresas uma de cada vez. Inteligência artificial, automação, novos padrões de consumo, eventos climáticos extremos e alterações no crédito ou na regulação podem se sobrepor e alterar, ao mesmo tempo, custos, mercados e formas de trabalho.",
        "O contexto não é cenário estático. É parte ativa do sistema: muda, reage, sofre interferências, produz efeitos e pode se transformar durante o próprio processo de análise.",
    ],
};

export const EPISTEMICO = {
    evidencia: "Evidência empírica/factual",
    interpretacao: "Interpretação científica",
    hipotese: "Hipótese em investigação",
    "lente-teorica": "Lente teórica",
    "lente-filosofica": "Lente filosófica",
};

export const ULTIMAS = { label: "Radar" };
